import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  FlatList,
  ActivityIndicator,
  Alert,
  StyleSheet,
} from "react-native";
import { auth, db } from "@/lib/firebase";
import NamePromptModal from "@/components/admin/NamePromptModal";
import {
  type ModerationStatus,
  adminListCategories,
  adminListSubcategories,
  adminCreateApprovedSubcategory,
  adminSubcategoryNameExists,
  adminRenameSubcategory,
  adminApproveSubcategory,
  adminRejectSubcategory,
  adminSoftDeleteSubcategory,
  adminRestoreSubcategory,
} from "@/lib/taxonomyAdmin";

type Cat = any;
type Sub = any;

const STATUS_TABS: ModerationStatus[] = ["approved", "pending", "rejected"];

export default function AdminSubcategoriesManager() {
  const adminUid = auth.currentUser?.uid;

  const [categories, setCategories] = useState<Cat[]>([]);
  const [catLoading, setCatLoading] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const [status, setStatus] = useState<ModerationStatus>("approved");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Sub[]>([]);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editing, setEditing] = useState<Sub | null>(null);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId]
  );

  function requireCategoryId(): string {
    const id = selectedCategoryId ?? "";
    if (!id) throw new Error("Pick a category first.");
    return id;
  }

  async function loadCategories() {
    if (!adminUid) return;
    setCatLoading(true);
    try {
      // For selector, show approved categories only (non-deleted)
      const data = await adminListCategories(db, { status: "approved", includeDeleted: false });
      setCategories(data);
      if (!selectedCategoryId && data.length) setSelectedCategoryId(data[0].id);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load categories.");
    } finally {
      setCatLoading(false);
    }
  }

  async function loadSubs() {
    if (!adminUid) return;
    if (!selectedCategoryId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await adminListSubcategories(db, {
        categoryId: selectedCategoryId, // ✅ nested path requires this
        status,
        includeDeleted,
      });
      setRows(data);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to load subcategories.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid]);

  useEffect(() => {
    loadSubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid, selectedCategoryId, status, includeDeleted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r?.name ?? "").toLowerCase().includes(q));
  }, [rows, search]);

  function openAdd() {
    if (!selectedCategoryId) {
      Alert.alert("Select category", "Pick a category first.");
      return;
    }
    setModalMode("add");
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(sub: Sub) {
    setModalMode("edit");
    setEditing(sub);
    setModalOpen(true);
  }

  async function handleSaveName(name: string) {
    if (!adminUid) return;

    try {
      const categoryId = requireCategoryId();

      if (modalMode === "add") {
        const exists = await adminSubcategoryNameExists(db, categoryId, name);
        if (exists) {
          Alert.alert("Already exists", `"${name}" is already in subcategories for this category.`);
          return;
        }

        await adminCreateApprovedSubcategory(db, {
          categoryId,
          name,
          adminUid,
        });
      } else if (editing?.id) {
        // ✅ now needs categoryId + subcategoryId (nested doc)
        await adminRenameSubcategory(db, {
          categoryId,
          subcategoryId: editing.id,
          name,
          adminUid,
        });
      }

      setModalOpen(false);
      await loadSubs();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Failed to save.");
    }
  }

  async function approve(sub: Sub) {
    if (!adminUid) return;
    try {
      const categoryId = requireCategoryId();
      await adminApproveSubcategory(db, { categoryId, subcategoryId: sub.id, adminUid });
      await loadSubs();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Approve failed.");
    }
  }

  async function reject(sub: Sub) {
    if (!adminUid) return;
    Alert.alert("Reject subcategory?", `Reject "${sub.name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            const categoryId = requireCategoryId();
            await adminRejectSubcategory(db, { categoryId, subcategoryId: sub.id, adminUid });
            await loadSubs();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Reject failed.");
          }
        },
      },
    ]);
  }

  async function toggleDelete(sub: Sub) {
    if (!adminUid) return;

    try {
      const categoryId = requireCategoryId();

      if (sub.isDeleted) {
        await adminRestoreSubcategory(db, { categoryId, subcategoryId: sub.id, adminUid });
        await loadSubs();
        return;
      }

      Alert.alert("Delete subcategory?", `Soft delete "${sub.name}"?`, [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await adminSoftDeleteSubcategory(db, { categoryId, subcategoryId: sub.id, adminUid });
              await loadSubs();
            } catch (e: any) {
              Alert.alert("Error", e?.message ?? "Delete failed.");
            }
          },
        },
      ]);
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Action failed.");
    }
  }

  return (
    <View style={{ flex: 1 }}>
      {/* Category selector */}
      {catLoading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading categories…</Text>
        </View>
      ) : (
        <View style={styles.selectorRow}>
          <Text style={styles.selectorLabel}>Category:</Text>
          <View style={styles.selectorWrap}>
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={categories}
              keyExtractor={(i) => i.id}
              contentContainerStyle={{ gap: 8 }}
              renderItem={({ item }) => {
                const active = item.id === selectedCategoryId;
                return (
                  <Pressable
                    onPress={() => setSelectedCategoryId(item.id)}
                    style={[styles.catPill, active && styles.catPillActive]}
                  >
                    <Text style={[styles.catPillText, active && styles.catPillTextActive]}>
                      {item.name}
                    </Text>
                  </Pressable>
                );
              }}
            />
          </View>

          <Pressable onPress={loadCategories} style={styles.ghostBtn}>
            <Text style={styles.ghostBtnText}>Refresh</Text>
          </Pressable>
        </View>
      )}

      {/* Status tabs */}
      <View style={styles.row}>
        {STATUS_TABS.map((s) => (
          <Pressable
            key={s}
            onPress={() => setStatus(s)}
            style={[styles.pill, status === s && styles.pillActive]}
          >
            <Text style={[styles.pillText, status === s && styles.pillTextActive]}>
              {s.toUpperCase()}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Search + add */}
      <View style={styles.row}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search subcategories…"
          style={[styles.input, { flex: 1 }]}
        />
        <Pressable onPress={openAdd} style={styles.primaryBtn}>
          <Text style={styles.primaryBtnText}>+ Add</Text>
        </Pressable>
      </View>

      {/* toggles */}
      <View style={styles.row}>
        <Pressable
          onPress={() => setIncludeDeleted((v) => !v)}
          style={[styles.toggle, includeDeleted && styles.toggleOn]}
        >
          <Text style={[styles.toggleText, includeDeleted && styles.toggleTextOn]}>
            Include Deleted: {includeDeleted ? "ON" : "OFF"}
          </Text>
        </Pressable>

        <Pressable onPress={loadSubs} style={styles.ghostBtn}>
          <Text style={styles.ghostBtnText}>Refresh</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading…</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
          renderItem={({ item }) => (
            <View style={[styles.card, item.isDeleted && { opacity: 0.6 }]}>
              <View style={{ flex: 1 }}>
                <Text style={styles.cardTitle}>
                  {item.name} {item.isDeleted ? <Text style={styles.badge}>DELETED</Text> : null}
                </Text>
                <Text style={styles.meta}>
                  status: {item.status} · id: {item.id}
                </Text>
              </View>

              <View style={styles.actions}>
                <Pressable onPress={() => openEdit(item)} style={styles.btn}>
                  <Text style={styles.btnText}>Edit</Text>
                </Pressable>

                {item.status === "pending" ? (
                  <>
                    <Pressable onPress={() => approve(item)} style={[styles.btn, styles.btnGood]}>
                      <Text style={[styles.btnText, styles.btnTextGood]}>Approve</Text>
                    </Pressable>
                    <Pressable onPress={() => reject(item)} style={[styles.btn, styles.btnBad]}>
                      <Text style={[styles.btnText, styles.btnTextBad]}>Reject</Text>
                    </Pressable>
                  </>
                ) : null}

                <Pressable onPress={() => toggleDelete(item)} style={[styles.btn, styles.btnBad]}>
                  <Text style={[styles.btnText, styles.btnTextBad]}>
                    {item.isDeleted ? "Restore" : "Delete"}
                  </Text>
                </Pressable>
              </View>
            </View>
          )}
          ListEmptyComponent={
            <View style={{ padding: 16 }}>
              <Text style={{ opacity: 0.7 }}>
                {selectedCategory
                  ? `No subcategories for "${selectedCategory.name}".`
                  : "Pick a category."}
              </Text>
            </View>
          }
        />
      )}

      <NamePromptModal
        visible={modalOpen}
        title={modalMode === "add" ? "Add Subcategory (Approved)" : "Edit Subcategory"}
        description={
          modalMode === "add"
            ? `Category: ${selectedCategory?.name ?? "-"} (created as APPROVED)`
            : "Rename the subcategory (keeps same ID)."
        }
        initialValue={modalMode === "edit" ? editing?.name : ""}
        confirmText={modalMode === "add" ? "Create" : "Save"}
        onCancel={() => setModalOpen(false)}
        onConfirm={handleSaveName}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  selectorRow: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 10 },
  selectorLabel: { fontWeight: "900" },
  selectorWrap: { flex: 1 },

  catPill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  catPillActive: { backgroundColor: "#111", borderColor: "#111" },
  catPillText: { fontWeight: "900", fontSize: 12 },
  catPillTextActive: { color: "white" },

  row: { flexDirection: "row", gap: 10, alignItems: "center", marginBottom: 10 },
  pill: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 999,
    paddingVertical: 7,
    paddingHorizontal: 10,
    backgroundColor: "#fafafa",
  },
  pillActive: { backgroundColor: "#111", borderColor: "#111" },
  pillText: { fontWeight: "900", fontSize: 12 },
  pillTextActive: { color: "white" },

  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  primaryBtn: {
    backgroundColor: "#111",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  primaryBtnText: { color: "white", fontWeight: "900" },

  ghostBtn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  ghostBtnText: { fontWeight: "900" },

  toggle: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: "#fafafa",
  },
  toggleOn: { backgroundColor: "#111", borderColor: "#111" },
  toggleText: { fontWeight: "900" },
  toggleTextOn: { color: "white" },

  loadingRow: { flexDirection: "row", gap: 8, alignItems: "center", padding: 12 },

  card: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
    backgroundColor: "white",
  },
  cardTitle: { fontSize: 15, fontWeight: "900" },
  badge: { fontSize: 12, fontWeight: "900", opacity: 0.6 },
  meta: { marginTop: 4, opacity: 0.7, fontSize: 12 },

  actions: { gap: 8, alignItems: "flex-end" },
  btn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  btnText: { fontWeight: "900" },
  btnGood: { borderColor: "#2e7d32" },
  btnTextGood: { color: "#2e7d32" },
  btnBad: { borderColor: "#c62828" },
  btnTextBad: { color: "#c62828" },
});
